import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  Customer,
  CustomerHourlyRate,
  CustomerHourlyRateAssignmentSave,
  CustomerHourlyRatePerson,
  CustomerMaterialItem
} from '../Models/customer.model';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private apiUrl = environment.apiBaseUrl + '/customer';
  private sbomApiUrl = environment.apiBaseUrl + '/sbom';

  constructor(private http: HttpClient) { }

  getCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.apiUrl);
  }

  getCustomer(id: number): Observable<Customer> {
    return this.http.get<Customer>(`${this.apiUrl}/${id}`);
  }

  createCustomer(customer: Customer): Observable<Customer> {
    const payload = {
      name: customer.name,
      type: customer.type ?? 'Normal',
      address: customer.address,
      contactPersonName: customer.contactPersonName,
      contactPersonPhone: customer.contactPersonPhone,
      contactPersonEmail: customer.contactPersonEmail,
      invoiceName: customer.invoiceName,
      invoiceAddress: customer.invoiceAddress,
      shippingName: customer.shippingName,
      shippingAddress: customer.shippingAddress,
      customerNumber: customer.customerNumber,
      vatId: customer.vatId,
      orderNumber: customer.orderNumber,
      orderDate: customer.orderDate,
      projectNumber: customer.projectNumber,
      projectManagerName: customer.projectManagerName,
      projectManagerPhone: customer.projectManagerPhone,
      projectManagerEmail: customer.projectManagerEmail,
      invoiceEmail: customer.invoiceEmail,
      paymentTerms: customer.paymentTerms,
      deliveryTerms: customer.deliveryTerms,
      invoiceNote: customer.invoiceNote,
      contactPersons: customer.contactPersons ?? []
    };
    return this.http.post<Customer>(this.apiUrl, payload);
  }

  updateCustomer(id: number, customer: Customer): Observable<Customer> {
    const payload = {
      name: customer.name,
      type: customer.type ?? 'Normal',
      address: customer.address,
      contactPersonName: customer.contactPersonName,
      contactPersonPhone: customer.contactPersonPhone,
      contactPersonEmail: customer.contactPersonEmail,
      invoiceName: customer.invoiceName,
      invoiceAddress: customer.invoiceAddress,
      shippingName: customer.shippingName,
      shippingAddress: customer.shippingAddress,
      customerNumber: customer.customerNumber,
      vatId: customer.vatId,
      orderNumber: customer.orderNumber,
      orderDate: customer.orderDate,
      projectNumber: customer.projectNumber,
      projectManagerName: customer.projectManagerName,
      projectManagerPhone: customer.projectManagerPhone,
      projectManagerEmail: customer.projectManagerEmail,
      invoiceEmail: customer.invoiceEmail,
      paymentTerms: customer.paymentTerms,
      deliveryTerms: customer.deliveryTerms,
      invoiceNote: customer.invoiceNote,
      contactPersons: customer.contactPersons ?? []
    };
    return this.http.put<Customer>(`${this.apiUrl}/${id}`, payload);
  }

  deleteCustomer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  getMaterialList(customerId: number): Observable<CustomerMaterialItem[]> {
    return this.http.get<CustomerMaterialItem[]>(`${this.apiUrl}/${customerId}/materials`);
  }

  addMaterial(customerId: number, material: Omit<CustomerMaterialItem, 'id'>): Observable<CustomerMaterialItem> {
    return this.http.post<CustomerMaterialItem>(`${this.apiUrl}/${customerId}/materials`, material);
  }

  updateMaterial(customerId: number, materialId: number, material: Omit<CustomerMaterialItem, 'id'>): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${customerId}/materials/${materialId}`, material);
  }

  deleteMaterial(customerId: number, materialId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${customerId}/materials/${materialId}`);
  }

  getHourlyRates(customerId: number): Observable<CustomerHourlyRate[]> {
    return this.http.get<CustomerHourlyRate[]>(`${this.apiUrl}/${customerId}/hourly-rates`);
  }

  addHourlyRate(customerId: number, rate: Omit<CustomerHourlyRate, 'id'>): Observable<CustomerHourlyRate> {
    return this.http.post<CustomerHourlyRate>(`${this.apiUrl}/${customerId}/hourly-rates`, rate);
  }

  updateHourlyRate(customerId: number, rateId: number, rate: Omit<CustomerHourlyRate, 'id'>): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${customerId}/hourly-rates/${rateId}`, rate);
  }

  /**
   * Personen dieses Takeda-Stammkunden mit ihrer Stundensatz-Zuordnung
   * (Team-Mitglieder + Arbeiter ohne Login, auch ohne Zuordnung).
   */
  getHourlyRateAssignments(customerId: number): Observable<CustomerHourlyRatePerson[]> {
    return this.http.get<CustomerHourlyRatePerson[]>(
      `${this.apiUrl}/${customerId}/hourly-rate-assignments`);
  }

  /**
   * Zuordnung einer Person setzen oder entfernen (Upsert).
   * `customerHourlyRateId: null` = "Nicht zugeordnet"; löscht nur die
   * Zuordnungszeile, nie den Stundensatz oder die Person.
   */
  setHourlyRateAssignment(
    customerId: number,
    assignment: CustomerHourlyRateAssignmentSave
  ): Observable<CustomerHourlyRatePerson> {
    return this.http.put<CustomerHourlyRatePerson>(
      `${this.apiUrl}/${customerId}/hourly-rate-assignments`, assignment);
  }

  deleteHourlyRate(customerId: number, rateId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${customerId}/hourly-rates/${rateId}`);
  }

  // Logo
  uploadLogo(customerId: number, file: File): Observable<{ logoPath: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<{ logoPath: string }>(`${this.sbomApiUrl}/customer/${customerId}/logo`, formData);
  }

  deleteLogo(customerId: number): Observable<void> {
    return this.http.delete<void>(`${this.sbomApiUrl}/customer/${customerId}/logo`);
  }

  getLogoUrl(logoPath: string): string {
    return `${this.sbomApiUrl}/customer-logos/${logoPath}`;
  }
}
